import format from 'date-fns/format';
import ru from 'date-fns/locale/ru';
import { formatInTimeZone } from 'date-fns-tz';
import { FormField, Header, HStack, Text, VStack } from '@direct-frontend/components';
import { type BaseValidationError, useField, useFormInstance } from '@adv-frontend/form-kit';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Проблема в текущем файле, тк OrderForm должен быть в widgets
import { LoginButton } from '@/features/LoginButton';
import type { SchemaDigitalOrder, SchemaDigitalOrderItem } from '@/shared/api/openApi';
import { Footer } from '@/shared/components/Footer';
import { formatPrice } from '@/shared/lib/formatPrice';
import { getRequestContext } from '@/shared/lib/requestContext';
import { OrderClientInput } from './components/OrderClientInput';
import { digitalOrderForm } from './model/forms/digitalOrderForm';
import { OrderDigitalProduct } from './components';
import { DIGITAL_ORDER_FORM_ID } from './constants';
import styles from './DigitalOrderForm.module.css';

interface DigitalOrderFormProps {
    order: SchemaDigitalOrder;
    items: SchemaDigitalOrderItem[];
}

const ERRORS_MAPPING: Record<string, string> = {
    INVALID_PHONE_FORMAT: 'Неверный формат телефона',
    INVALID_EMAIL_FORMAT: 'Неверный формат email',
    REQUIRED_CLIENT: 'Необходимо заполнить все данные отправителя',
} as const;

const buildErrorMessage = (errors: BaseValidationError[]): string | undefined => {
    const [validationError] = errors;

    if (validationError?.code) {
        return ERRORS_MAPPING[validationError.code] ?? validationError.code;
    }

    return undefined;
};

const MapGMTToMSK = {
    'GMT-12': '(МСК+10)',
    'GMT-11': '(МСК+11)',
    'GMT-10': '(МСК+12)',
    'GMT-9': '(МСК-12)',
    'GMT-8': '(МСК-11)',
    'GMT-7': '(МСК-10)',
    'GMT-6': '(МСК-9)',
    'GMT-5': '(МСК-8)',
    'GMT-4': '(МСК-7)',
    'GMT-3': '(МСК-6)',
    'GMT-2': '(МСК-5)',
    'GMT-1': '(МСК-4)',
    GMT: '(МСК-3)',
    'GMT+1': '(МСК-2)',
    'GMT+2': 'время Калининградское',
    'GMT+3': 'время Московское',
    'GMT+4': 'время Самарское',
    'GMT+5': 'время Екатеринбургское',
    'GMT+6': 'время Омское',
    'GMT+7': 'время Красноярское',
    'GMT+8': 'время Иркутское',
    'GMT+9': 'время Якутское',
    'GMT+10': 'время Владивостокское',
    'GMT+11': 'время Среднеколымское',
    'GMT+12': 'время Камчатское',
};

const formatDate = (date: string) =>
    format(new Date(date), 'd MMMM yyyy', {
        locale: ru,
    });

const formatTime = (date: string) => format(new Date(date), 'HH:mm');

const formatDateTimeZone = (date: string) => formatInTimeZone(new Date(date), 'Europe/Moscow', 'zzz');

const getMSKTimeZone = (timeZone: string) => {
    if (timeZone in MapGMTToMSK) {
        return MapGMTToMSK[timeZone as keyof typeof MapGMTToMSK];
    }

    return timeZone;
};

export const DigitalOrderForm = (props: DigitalOrderFormProps) => {
    const { order } = props;
    const { fields } = useFormInstance({ form: digitalOrderForm, id: DIGITAL_ORDER_FORM_ID });
    const { account } = getRequestContext();
    const fieldClient = useField(fields.client);

    return (
        <VStack justifyContent="space-between" className={styles.container}>
            <VStack gap="28">
                {!account.loggedIn && <LoginButton />}
                <VStack gap="8">
                    <HStack gap="16" justifyContent="space-between" alignItems="center">
                        <Header level="h3">Оформление заказа</Header>
                    </HStack>
                    <VStack gap="12">
                        <FormField size="s" errorMessage={buildErrorMessage(fieldClient.state.errors)}>
                            {(fieldProps) => (
                                <OrderClientInput
                                    {...fieldProps}
                                    value={fieldClient.value}
                                    onChange={fields.client.setValue}
                                    validationState={buildErrorMessage(fieldClient.state.errors) ? 'invalid' : 'valid'}
                                />
                            )}
                        </FormField>
                    </VStack>
                </VStack>
                <VStack gap="48">
                    <VStack>
                        <VStack gap="8" className={styles.orderItemsHeader}>
                            <Header level="h3">Состав заказа</Header>
                            <HStack alignItems="center" className={styles.info}>
                                {order.send_date ? (
                                    <Text size="mc">
                                        Подарочная карта будет отправлена на почту {order.holder_email}{' '}
                                        {formatDate(order.send_date)} в {formatTime(order.send_date)}{' '}
                                        {getMSKTimeZone(formatDateTimeZone(order.send_date))}
                                    </Text>
                                ) : (
                                    <Text size="mc">
                                        Подарочная карта будет отправлена на почту {order.holder_email} сразу после
                                        оплаты
                                    </Text>
                                )}
                            </HStack>
                        </VStack>
                        <VStack>
                            <OrderDigitalProduct
                                name={`Подарочная карта номиналом ${formatPrice((100 * Number(order.total_final_price)) / 100)}`}
                                quantity={1}
                                finalPrice={order.total_final_price}
                                media={order.items[0]?.media}
                            />
                        </VStack>
                    </VStack>

                    <Footer hideOnTouch />
                </VStack>
            </VStack>
        </VStack>
    );
};
